import * as actionTypes from "./claimsActionTypes";
import * as actions from "./claimsActions";
import { createAction } from "@reduxjs/toolkit";
import * as logger from "common/logger";
import * as config from "config";
import * as FileSaver from "file-saver";
import * as fileUtils from "fileUtils";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as utils from "utils";

export const useFilterAsBaseLayer = () => (dispatch, getState) => {
  const filter = claimsSelectors.selectClaimsFilter(getState());
  dispatch({
    type: actionTypes.USE_FILTER_AS_BASE_LAYER,
    payload: filter,
  });
  dispatch(pricingActions.recalculateInsuredExpectedEvents());
};

export const setV2ClaimsKey = (
  claimsKey,
  fileId,
  tableName,
  uploadedFilename,
  tablesList,
  originalFileId = null,
  originalFilename = null,
  extractionDetails = null
) => ({
  type: actionTypes.SET_V2_CLAIMS_KEY,
  payload: {
    claimsKey,
    fileId,
    tableName,
    uploadedFilename,
    tablesList,
    originalFileId,
    originalFilename,
    extractionDetails,
  },
});

export const updateSelfInsuredRetention = (retention) => ({
  type: actionTypes.UPDATE_SELF_INSURED_RETENTION,
  payload: {
    retention,
  },
});

export const deleteSelfInsuredRetention = (id) => ({
  type: actionTypes.DELETE_SELF_INSURED_RETENTION,
  payload: { id },
});

export const setSelfInsuredRetentions = (retentions) => ({
  type: actionTypes.SET_SELF_INSURED_RETENTIONS,
  payload: retentions,
});

export const updateClaimsFilter = (filter, value) => ({
  type: actionTypes.UPDATE_CLAIMS_FILTER,
  payload: { filter, value },
});

export const clearClaimsFilter = () => ({
  type: actionTypes.CLEAR_CLAIMS_FILTER,
});

export const updateClaimThreshold = (threshold) => (dispatch) => {
  dispatch({
    type: actionTypes.UPDATE_CLAIM_THRESHOLD,
    payload: threshold,
  });
};

export const updateDeltaClaimThreshold = (threshold) => (dispatch) => {
  dispatch({
    type: actionTypes.UPDATE_DELTA_CLAIM_THRESHOLD,
    payload: threshold,
  });
};

export const updateAiClaimsMatches = (matches) => (dispatch) => {
  dispatch({
    type: actionTypes.UPDATE_AI_CLAIMS_MATCHES,
    payload: matches,
  });
};

export const updateAiMatchingData = (data) => (dispatch) => {
  dispatch({
    type: actionTypes.UPDATE_AI_MATCHING_DATA,
    payload: data,
  });
};

export const updateManualClaimsMatches = (matches) => (dispatch) => {
  dispatch({
    type: actionTypes.UPDATE_MANUAL_CLAIMS_MATCHES,
    payload: matches,
  });
};

export const updateTotalLossNumberFormat = createAction(
  "activeClaimsFile/updateTotalLossNumberFormat",
  (numberFormat) => {
    return {
      payload: numberFormat,
    };
  }
);

export const saveFileAs = (blob, name) => {
  return FileSaver.saveAs(blob, name);
};

export const downloadOriginalFromFiles = (id, name, key) => (dispatch) => {
  dispatch({
    type: actionTypes.DOWNLOAD_ORIGINAL_CLAIMS_DATA_STARTED,
    payload: key,
  });
  fileUtils
    .saveFile(id, name)
    .then(() => {
      dispatch({
        type: actionTypes.DOWNLOAD_ORIGINAL_CLAIMS_DATA_RETURNED,
        payload: key,
      });
    })
    .catch((error) => {
      dispatch(actions.downloadOriginalError(error, key));
    });
};

export const downloadOriginal = (uploadedFilename, file, key) => (dispatch) => {
  dispatch({
    type: actionTypes.DOWNLOAD_ORIGINAL_CLAIMS_DATA_STARTED,
    payload: key,
  });
  utils
    .authenticatedFetch(
      config.endpoints.claims(
        "download/original?" + new URLSearchParams({ uploadedFilename })
      )
    )
    .then((response) => response.json())
    .then((json) => fetch(json.aws))
    .then((response) => response.blob())
    .then((blob) => actions.saveFileAs(blob, file))
    .then(() => {
      dispatch({
        type: actionTypes.DOWNLOAD_ORIGINAL_CLAIMS_DATA_RETURNED,
        payload: key,
      });
    })
    .catch((e) => dispatch(actions.downloadOriginalError(e, key)));
};

export const downloadOriginalError = (e, key) => (dispatch) => {
  dispatch({
    type: actionTypes.DOWNLOAD_ORIGINAL_CLAIMS_DATA_FAILED,
    payload: key,
  });
  logger.exception(e);
};

export const claimsFileChecked = (key, checked) => ({
  type: actionTypes.CLAIMS_FILE_CHECKED,
  payload: {
    key,
    checked,
  },
});

export const updateClaimsNote = (note) => ({
  type: actionTypes.UPDATE_CLAIMS_NOTE,
  payload: note,
});

export const updateClaimsFields = (fields) => ({
  type: actionTypes.UPDATE_CLAIMS_FIELDS,
  payload: fields,
});

export const createManualClaim = (claim) => ({
  type: actionTypes.MANUAL_CREATE_MERGE_ENTRY,
  payload: claim,
});

export const handleChangeLossYear = (value, index) => ({
  type: actionTypes.MANUAL_UPDATE_LOSS_YEAR,
  payload: {
    lossYear: value,
    index,
  },
});

export const deleteManualClaims = (index) => ({
  type: actionTypes.DELETE_MANUAL_CLAIMS,
  payload: {
    index,
  },
});

export const handleChangeCoverage = (value, index) => ({
  type: actionTypes.MANUAL_UPDATE_COVERAGE,
  payload: {
    coverage: value,
    index,
  },
});

export const handleChangeLoss = (value, index) => ({
  type: actionTypes.MANUAL_UPDATE_LOSS,
  payload: {
    lossAmount: value,
    index,
  },
});

export const handleChangeDescription = (value, index) => ({
  type: actionTypes.MANUAL_UPDATE_DESCRIPTION,
  payload: {
    description: value,
    index,
  },
});
export const handleChangeClaimant = (value, index) => ({
  type: actionTypes.MANUAL_UPDATE_CLAIMANT,
  payload: {
    claimant: value,
    index,
  },
});
export const handleChangeOC = (value, index) => ({
  type: actionTypes.MANUAL_UPDATE_OPEN_CLOSED,
  payload: {
    oc: value,
    index,
  },
});

export const handleChangeDateClosed = (value, index) => ({
  type: actionTypes.MANUAL_UPDATE_DATE_CLOSED,
  payload: {
    dateClosed: value,
    index,
  },
});

export const toggleClaimExclude = (key, index, excluded) => ({
  type: actionTypes.TOGGLE_CLAIM_EXCLUSION,
  payload: {
    key,
    index,
    excluded,
  },
});

export const updateClaimOverride = (key, index, value) => ({
  type: actionTypes.OVERIDE_CLAIM_VALUE,
  payload: {
    key,
    index,
    value,
  },
});

export const updateClaimReportedLossOverride = (key, index, value) => ({
  type: actionTypes.OVERRIDE_REPORTED_LOSS_CLAIM_VALUE,
  payload: {
    key,
    index,
    value,
  },
});

export const setClaimOpenClosedOverride = (key, index, value) => ({
  type: actionTypes.SET_CLAIM_OPEN_CLOSED_OVERRIDE,
  payload: {
    key,
    index,
    value,
  },
});

export const removeClaimOpenClosedOverride = (key, index) => ({
  type: actionTypes.REMOVE_CLAIM_OPEN_CLOSED_OVERRIDE,
  payload: {
    key,
    index,
  },
});

export const addLossYear = (year) => (dispatch) => {
  dispatch({
    type: actionTypes.CLAIMS_TABLE_ADD_LOSS_YEAR,
    payload: year,
  });
};

export const removeLossYear = (year) => (dispatch) => {
  dispatch({
    type: actionTypes.CLAIMS_TABLE_REMOVE_LOSS_YEAR,
    payload: year,
  });
};

export const hideClaimsFile = (key) => ({
  type: actionTypes.HIDE_CLAIMS_FILE,
  payload: key,
});

export const updateChartZoom = (name, zoom) => ({
  type: actionTypes.UPDATE_CLAIMS_ZOOM,
  payload: {
    name,
    zoom,
  },
});

export const clearChartZoom = (name) => ({
  type: actionTypes.CLEAR_CHART_ZOOM,
  payload: name,
});

export const setInflationModel = (inflationModel) => ({
  type: actionTypes.SET_INFLATION_MODEL,
  payload: inflationModel,
});

export const showClaimsFileColumnMappingDisplay = (claimsKey) => (dispatch) => {
  const claimsMapping = claimsSelectors.selectClaimsFileColumnMappingDisplay();
  if (claimsMapping.claimsKey !== claimsKey || claimsMapping.state === "IDLE") {
    dispatch({
      type: actionTypes.SHOW_CLAIMS_FILE_COLUMN_MAPPING_DISPLAY,
      payload: claimsKey,
    });
    dispatch({
      type: actionTypes.START_CLAIMS_FILE_COLUMN_MAPPING_DISPLAY_LOAD,
    });
  }
};

export const hideClaimsFileColumnMappingDisplay = () => (dispatch) => {
  dispatch({
    type: actionTypes.HIDE_CLAIMS_FILE_COLUMN_MAPPING_DISPLAY,
    payload: null,
  });
};

export const updateAggregatedClaims = (values) => ({
  type: actionTypes.UPDATE_AGGREGATED_CLAIMS,
  payload: values,
});

export const setDuplicateGroups = (groups) => ({
  type: actionTypes.SET_DUPLICATE_GROUPS,
  payload: groups,
});

export const setIgnoredDuplicates = (ignoredIds) => ({
  type: actionTypes.SET_IGNORED_DUPLICATES,
  payload: ignoredIds,
});

export const updateAcceptedDuplicates = (accepted) => ({
  type: actionTypes.UPDATE_ACCEPTED_DUPLICATES,
  payload: accepted,
});
